import { ForwardedRef, forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react'
import Webcam from 'react-webcam'
import { Box } from '@chakra-ui/react'

export type CamerRefHandle = {
  changeCamera: () => void
  capture: () => Promise<CapturedImage | undefined>
}

export type CapturedImage = {
  imageSrc: string
  imageBlob: Blob
}

// eslint-disable-next-line @typescript-eslint/ban-types
type Props = {}

export default forwardRef<CamerRefHandle, Props>(function Camera(
  props: Props,
  ref: ForwardedRef<CamerRefHandle>,
): JSX.Element {
  useImperativeHandle(ref, () => ({
    changeCamera: changeVideoConstraints,
    capture,
  }))

  const webcamRef = useRef<Webcam>(null)
  const [currentFacingMode, setCurrentFacingMode] = useState<'user' | { exact: 'environment' }>({
    exact: 'environment',
  })

  const base64toBlob = async (base64Data: string) => {
    const base64Response = await fetch(base64Data)
    const blob = await base64Response.blob()
    return blob
  }

  const changeVideoConstraints = () => {
    if (currentFacingMode !== 'user') {
      setCurrentFacingMode('user')
    } else {
      setCurrentFacingMode({ exact: 'environment' })
    }
  }

  const capture = useCallback(async (): Promise<CapturedImage | undefined> => {
    const imageSrc = webcamRef.current?.getScreenshot()
    if (imageSrc) {
      const blob = await base64toBlob(imageSrc)
      return {
        imageSrc,
        imageBlob: blob,
      }
    }
    return undefined
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webcamRef])

  return (
    <Box display="flex" justifyContent="center" height={'100%'} width={'100%'}>
      <Webcam
        audio={false}
        ref={webcamRef}
        screenshotFormat="image/jpeg"
        screenshotQuality={1}
        forceScreenshotSourceSize
        videoConstraints={{ facingMode: currentFacingMode, width: 1080 }}
        style={{ height: '100%', objectFit: 'scale-down' }}
      />
    </Box>
  )
})

// export default Camera
